import { useCallback, useEffect, useRef, useState } from 'react';

// Web Speech API types — not yet in TypeScript's dom lib
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: { readonly transcript: string };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  readonly [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent {
  readonly error: string;
}

interface SpeechRecognitionInstance {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  start(): void;
  stop(): void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

function getSpeechRecognitionConstructor():
  | SpeechRecognitionConstructor
  | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition;
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const uaData = (
    navigator as Navigator & {
      userAgentData?: { mobile?: boolean };
    }
  ).userAgentData;
  if (typeof uaData?.mobile === 'boolean') {
    return uaData.mobile;
  }
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export function useSpeechRecognition({
  onTranscript,
  lang = 'en-GB',
}: {
  onTranscript: (text: string) => void;
  lang?: string;
}): {
  isListening: boolean;
  isSupported: boolean;
  interimTranscript: string | null;
  start: () => void;
  stop: () => void;
  toggle: () => void;
} {
  const SpeechRecognitionConstructor = getSpeechRecognitionConstructor();
  const isSupported = SpeechRecognitionConstructor != null;

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState<string | null>(
    null
  );

  const resetSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      recognitionRef.current?.stop();
    }, 5000);
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognitionConstructor) return;
    if (recognitionRef.current) return;

    const recognition = new SpeechRecognitionConstructor();
    recognition.lang = lang;
    recognition.interimResults = true;
    // Chrome on Android emits each interim chunk as a separate final result
    // when `continuous = true`, causing transcripts to duplicate (P-7920).
    // Disable continuous mode on mobile so recognition completes one utterance
    // at a time.
    recognition.continuous = !isMobileDevice();

    recognition.onresult = (event) => {
      resetSilenceTimer();
      const result = event.results[event.resultIndex];
      if (!result) return;

      const text = result[0]?.transcript?.trim();
      if (!text) return;

      if (result.isFinal) {
        setInterimTranscript(null);
        onTranscript(text);
      } else {
        setInterimTranscript(text);
      }
    };

    recognition.onstart = () => {
      setIsListening(true);
      resetSilenceTimer();
    };
    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript(null);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }
    };
    recognition.onerror = (event) => {
      if (event.error !== 'aborted') {
        setIsListening(false);
        setInterimTranscript(null);
        if (recognitionRef.current === recognition) {
          recognitionRef.current = null;
        }
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [SpeechRecognitionConstructor, onTranscript, lang, resetSilenceTimer]);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
    } else {
      start();
    }
  }, [isListening, start, stop]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  return { isListening, isSupported, interimTranscript, start, stop, toggle };
}
